import { useCallback } from 'react';

import useApi from '../api';

/**
 * Hook para gerenciar operações de regras de visitas
 */
export const useEnterpiseVisitRulesApi = () => {
    const { api } = useApi();

    /**
     * Busca regras de visitas paginadas com filtros
     * @param {number} enterpriseId - ID da empresa
     * @param {number} page - Número da página (começa em 1)
     * @param {number} limit - Quantidade de itens por página
     * @param {Array} collaboratorIds - Array de IDs de colaboradores (opcional)
     * @param {Array} propertyIds - Array de IDs de propriedades (opcional)
     * @param {string} period - Período da visita: 'middle' ou 'fulltime' (opcional)
     * @returns {Promise} - Promise com os dados paginados
     */
    const fetchVisitRules = async (enterpriseId, page = 1, limit = 15, collaboratorIds = [], propertyIds = [], period = null) => {
        try {
            const params = new URLSearchParams({
                enterprise_id: enterpriseId,
                page,
                limit
            });

            // Adiciona filtros de collaborator_ids se fornecidos
            if (collaboratorIds && collaboratorIds.length > 0) {
                collaboratorIds.forEach((id) => params.append('collaborator_ids[]', id));
            }

            // Adiciona filtros de property_ids se fornecidos
            if (propertyIds && propertyIds.length > 0) {
                propertyIds.forEach((id) => params.append('property_ids[]', id));
            }

            // Adiciona filtro de period se fornecido
            if (period && (period === 'middle' || period === 'fulltime')) {
                params.append('period', period);
            }

            // currentUserRole para aplicar escopo de supervisor no backend
            const currentUserRole = Number(sessionStorage.getItem('currentUserRole'));
            if (currentUserRole) {
                params.append('currentUserRole', currentUserRole);
            }

            const response = await api.get(`/enterprise-visits-rules?${params.toString()}`);

            return {
                data: response.data.data,
                pagination: {
                    currentPage: response.data.pagination.current_page,
                    perPage: response.data.pagination.per_page,
                    total: response.data.pagination.total,
                    totalPages: response.data.pagination.total_pages,
                    from: response.data.pagination.from,
                    to: response.data.pagination.to
                }
            };
        } catch (error) {
            console.error('Erro ao buscar regras de visitas:', error);
            throw error;
        }
    };

    /**
     * Atualiza uma regra de visita
     * @param {number} id - ID da regra de visita
     * @param {object} updates - Dados para atualizar
     * @returns {Promise} - Promise com o item atualizado
     */
    const updateVisitRule = async (id, updates) => {
        try {
            const response = await api.put(`/enterprise-visits-rules/${id}`, updates);
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Erro ao atualizar regra de visita:', error);
            throw error;
        }
    };

    /**
     * Cria uma nova regra de visita
     * @param {object} data - Dados da nova regra
     * @returns {Promise} - Promise com o item criado
     */
    const createVisitRule = useCallback(
        async (data) => {
            try {
                const response = await api.post('/enterprise-visits-rules', data);
                return {
                    success: true,
                    data: response.data
                };
            } catch (error) {
                console.error('Erro ao criar regra de visita:', error);
                throw error;
            }
        },
        [api]
    );

    /**
     * Deleta uma regra de visita
     * @param {number} id - ID da regra de visita
     * @returns {Promise} - Promise com o resultado
     */
    const deleteVisitRule = useCallback(
        async (id) => {
            try {
                await api.delete(`/enterprise-visits-rules/${id}`);
                return {
                    success: true
                };
            } catch (error) {
                console.error('Erro ao deletar regra de visita:', error);
                throw error;
            }
        },
        [api]
    );

    /**
     * Ativa/desativa uma regra de visita
     * @param {number} id - ID da regra de visita
     * @param {boolean} isActive - Novo status
     * @returns {Promise} - Promise com o resultado
     */
    const toggleActiveVisitRule = useCallback(
        async (id, isActive) => {
            try {
                const response = await api.patch(`/enterprise-visits-rules/${id}/toggle-active`, {
                    is_active: isActive
                });
                return {
                    success: true,
                    data: response.data
                };
            } catch (error) {
                console.error('Erro ao alterar status da regra de visita:', error);
                throw error;
            }
        },
        [api]
    );

    return {
        fetchVisitRules,
        updateVisitRule,
        createVisitRule,
        deleteVisitRule,
        toggleActiveVisitRule
    };
};
